// Optimized Chatbot component with memoization and stable callbacks
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { IoMdSend, IoMdInformationCircleOutline } from "react-icons/io";
import { MdOutlineAttachment } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { ChatState, initialState, AiProfile } from "./chatState";
import { paths } from "./paths";
import TypingIndicator from "./TypingIndicator";
import {
  getCurrentUser,
  syncUserWithServer,
  updateUser,
  getCurrentOrganization,
} from "src/features/profile/state/profileSlice";
import { useAppDispatch } from "src/common/state/hooks";
import {
  resetActiveGptConversation,
  updateChat,
  askWixarpedia,
  setActiveGptConversation,
  fetchGptConversations,
  setChosenLlmProvider,
  getChosenLlmProvider,
  getScenarioHelperRagAgentIds,
  setScenarioHelperRagAgentIds,
  resetScenarioHelperRagAgentIds,
  startPlanModeChat,
  continuePlanModeChat,
  transitionToReady,
  refinePlanThunk,
  getScenarioHelperContextPdfs,
  getScenarioHelperContextImages,
  getPlanModeProgress,
  getGenerationStartedAt,
  addScenarioHelperContextPdf,
  addScenarioHelperContextImage,
  resetScenarioHelperContextPdfs,
  resetScenarioHelperContextImages,
} from "../state/chatbotSlice";
import ChatBubble from "./ChatBubble";
import GraphBubble from "./GraphBubble";
import { IGptConversation } from "src/model/model";
import PlanPreview from "./PlanPreview";
import GenerationRecap from "./GenerationRecap";
import PlanModeProgress from "./PlanModeProgress";
import SkipIntakeButton from "./SkipIntakeButton";
import { canSkipIntake } from "../utils/skipThreshold";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { useSelector, shallowEqual } from "react-redux";
import { GPT_CONVERSATION_LOADER_COMPONENT_ID } from "./GptConversationLoader";
import ModalStandard from "src/common/components/Modal/ModalStandard";
import CreateProjectFromAiGraph from "./CreateProjectFromAiGraph";
import { closeStandardModal, openStandardModal } from "src/common/state/slice/modal/modalSlice";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import GeneratePdfFromScenarioHelperResponse from "./GeneratePdfFromScenarioHelperResponse";
import { generateHashFromString } from "../utils/generateHashFromString";
import { isMarkdownShapedLikeAScenario } from "../utils/isMarkdownShapedLikeAScenario";
import { renderPlanToMarkdown } from "../utils/renderPlanToMarkdown";
import LlmProviderDropdown from "./LlmProviderDropdown";
import { Link, useNavigate } from "react-router-dom";
import { userMessageSignalsProceed } from "../utils/proceedIntent";
import {
  setCurrentProject,
  setCurrentProjectPermissions,
  getProjectPermissionsFromServer,
} from "src/features/projects/state/projectsSlice";
import { fetchSpecificProjectGroupWithProjects } from "src/features/projectGroups/state/projectGroupsSlice";
import { IProject } from "src/model/model";
import "./Chatbot.scss";
import getUserPreferredLlmProvider from "../utils/getUserPrefferedLlmProvider";
import { useDictation } from "src/features/tts-and-stt/hooks/useDictation";
import { useConversationMode } from "src/features/tts-and-stt/hooks/useConversationMode";
import MicrophoneButton from "src/features/tts-and-stt/components/MicrophoneButton";
import VoiceModeToggle from "src/features/tts-and-stt/components/VoiceModeToggle";
import SpeakingIndicator from "src/features/tts-and-stt/components/SpeakingIndicator";
import AttachContextMedia, { isMediaProcessing } from "./AttachContextMedia";
import { Tooltip } from "@mui/material";

interface Message {
  text: string;
  sender: "bot" | "user";
}

interface ChatbotProps {
  mode: "scenarioHelper" | "wixarpedia";
  activeConversation?: IGptConversation | null;
  onGenerateProject?: () => void;
}

export const CHATBOT_COMPONENT_ID = "chatbotComponentId";
export const createProjectFromAiGraphModalId = "createProjectFromAiGraphModalId";

const Chatbot: React.FC<ChatbotProps> = ({ mode, activeConversation, onGenerateProject }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isOpeningModule, setIsOpeningModule] = useState(false);

  const loadInitialMessages = useCallback((): Message[] => {
    if (mode === "wixarpedia") {
      const saved = localStorage.getItem("wixarpediaChatMessages");
      return saved
        ? JSON.parse(saved)
        : [{ text: "Bonjour ! Comment puis-je vous aider ?", sender: "bot" }];
    }
    return [];
  }, [mode]);

  const [chatState, setChatState] = useState<ChatState>(initialState);
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messages, setMessages] = useState<Message[]>(loadInitialMessages);
  const [userInput, setUserInput] = useState("");
  const [inputMode, setInputMode] = useState(true);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState<"metier" | "targetAudience">("metier");
  const [onboardingMetier, setOnboardingMetier] = useState("");
  const [onboardingCustomMetier, setOnboardingCustomMetier] = useState("");
  const [onboardingShowCustomMetier, setOnboardingShowCustomMetier] = useState(false);
  const [onboardingTargetAudience, setOnboardingTargetAudience] = useState("");
  const [sessionConversationId, setSessionConversationId] = useState<number | null>(null);

  const bottomRef = useRef<HTMLDivElement>(null);

  const user = useSelector(getCurrentUser, shallowEqual);
  const organization = useSelector(getCurrentOrganization);
  const chosenLlmProvider = useSelector(getChosenLlmProvider);
  const ragAgentIds = useSelector(getScenarioHelperRagAgentIds, shallowEqual);
  const contextPdfs = useSelector(getScenarioHelperContextPdfs);
  const contextImages = useSelector(getScenarioHelperContextImages);
  const planProgress = useSelector(getPlanModeProgress);
  const generationStartedAt = useSelector(getGenerationStartedAt);

  const phase = activeConversation?.plan_phase ?? null;
  const plan = activeConversation?.conversation_metadata?.plan;
  const isPlanMode = phase !== null && phase !== undefined;
  // Legacy / orphan plan-mode conversations have plan_phase === null.
  // The OG-flow chat is read-/replay-only with the deprecated
  // "Générer le module" markdown shortcut; the 17 plan-mode orphans
  // (plan in metadata, phase nulled by earlier dev iteration) are
  // effectively stuck. Either way, nudge the user to start fresh.
  const isLegacyConversation = Boolean(
    mode === "scenarioHelper" && activeConversation && !activeConversation.plan_phase,
  );
  const [legacyBannerDismissed, setLegacyBannerDismissed] = useState(false);
  // Reset the dismissal whenever the user switches to a different
  // conversation — otherwise dismissing once permanently hides the banner
  // for every legacy conversation in this session.
  useEffect(() => {
    setLegacyBannerDismissed(false);
  }, [activeConversation?.id]);
  // Ref tracks the latest phase so async submit handlers always read fresh
  // state instead of stale useCallback closures.
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const activeConversationRef = useRef(activeConversation);
  activeConversationRef.current = activeConversation;
  const [showIntakeHistory, setShowIntakeHistory] = useState(false);
  const [intakeHistoryBoundary, setIntakeHistoryBoundary] = useState<number | null>(null);
  const lastConvIdForBoundaryRef = useRef<number | undefined>(undefined);

  const hasMediaProcessing = useMemo(
    () => [...contextPdfs, ...contextImages].some(isMediaProcessing),
    [contextPdfs, contextImages],
  );

  const isSubmitDisabled = useMemo(
    () => !userInput.trim() || isSubmitting || hasMediaProcessing,
    [userInput, isSubmitting, hasMediaProcessing],
  );

  const submitDisabledReason = !userInput.trim()
    ? ""
    : hasMediaProcessing
    ? "Un fichier est en cours de traitement…"
    : "";

  // Refs bridge the cyclic dep between conversation (needs to call dictation.startListening)
  // and dictation (needs to call submit-with-text in auto-send path).
  const dictationRef = useRef<ReturnType<typeof useDictation> | null>(null);
  const submitWithTextRef = useRef<((text: string) => Promise<void>) | null>(null);

  const conversation = useConversationMode({
    onAutoListenCue: () => {
      dictationRef.current?.startListening().catch(() => undefined);
    },
    onDeactivate: () => {
      dictationRef.current?.stopListening().catch(() => undefined);
    },
  });

  const dictation = useDictation({
    // Conversation mode wants a short silence timeout so the user's reply auto-sends;
    // dictation mode wants a long one so thinking pauses don't cut the user off.
    silenceTimeout: conversation.voiceModeEnabled ? 1500 : 30000,
    onTranscript: (text, { viaTimeout }) => {
      if (conversation.voiceModeEnabled && viaTimeout && text.trim()) {
        submitWithTextRef.current?.(text).catch(() => undefined);
      } else {
        setUserInput(text);
      }
    },
  });
  dictationRef.current = dictation;

  // Mirror live transcript into userInput while listening so the textarea
  // has a single source of truth and never shows stale state.
  useEffect(() => {
    if (!dictation.isListening) return;
    const live = [dictation.transcript, dictation.interimTranscript].filter(Boolean).join(" ");
    setUserInput(live);
  }, [dictation.isListening, dictation.transcript, dictation.interimTranscript]);

  // Track whether a TTS streaming session is open for the current bot reply.
  const ttsSessionOpenRef = useRef(false);

  const getSavedAiProfile = useCallback((): AiProfile | null => {
    try {
      const prefs = (user as any)?.preferences;
      if (!prefs) return null;
      const obj = typeof prefs === "string" ? JSON.parse(prefs) : prefs;
      const profile = obj?.aiProfile;
      if (profile?.metier) return profile;
      return null;
    } catch {
      return null;
    }
  }, [user]);

  const orgCtx = useMemo(() => {
    const ctx = (organization as any)?.ai_context;
    if (!ctx || typeof ctx !== "object") return null;
    return ctx.sector || ctx.description ? ctx : null;
  }, [organization]);

  const hasUserProfile = useMemo(() => !!getSavedAiProfile()?.metier, [getSavedAiProfile]);

  const aiContextParts = useMemo(() => {
    const parts: string[] = [];
    const profile = getSavedAiProfile();
    if (profile?.metier) parts.push(profile.metier);
    if (profile?.targetAudience) parts.push(profile.targetAudience);
    if (orgCtx?.sector) parts.push(orgCtx.sector);
    if (orgCtx?.description) parts.push(orgCtx.description);
    return parts;
  }, [getSavedAiProfile, orgCtx]);

  // Persist messages for wixarpedia
  useEffect(() => {
    if (mode === "wixarpedia") {
      localStorage.setItem("wixarpediaChatMessages", JSON.stringify(messages));
    }
  }, [messages, mode]);

  // Initialize conversation on mode or activeConversation change
  useEffect(() => {
    if (mode === "scenarioHelper") {
      activeConversation ? loadPreviousConversation() : resetChatLocally();
    } else {
      resetChatLocally();
    }
  }, [mode, activeConversation]);

  // Scroll chat into view
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isBotTyping]);

  // Hydrate the chatbot slice from the user's last-known active conversation
  // on first load. Skip when the slice already holds the same conversation:
  // user.active_gpt_conversation is snapshotted at login and never re-fetched
  // mid-session, so in-session plan-mode transitions (intake →
  // ready_to_generate → generated) live only in the chatbot slice. Overwriting
  // on Chatbot remount (e.g. after navigating away and back) would revert
  // plan_phase to the stale login-time value.
  useEffect(() => {
    if (
      user?.active_gpt_conversation?.id &&
      user.active_gpt_conversation.chat_origin === "scenarioHelper" &&
      activeConversationRef.current?.id !== user.active_gpt_conversation.id
    ) {
      dispatch(setActiveGptConversation(user.active_gpt_conversation));
    }
  }, [user?.active_gpt_conversation?.id, dispatch]);

  // Set initial LLM provider
  useEffect(() => {
    const initial = getUserPreferredLlmProvider(user?.preferences);
    dispatch(setChosenLlmProvider(initial));
  }, [user?.preferences, dispatch]);

  // Ensure modal closed on mount
  useEffect(() => {
    dispatch(closeStandardModal(createProjectFromAiGraphModalId));
  }, [dispatch]);

  const loadPreviousConversation = useCallback(() => {
    if (!activeConversation) return;
    setShowOnboardingModal(false);
    const { conversation_metadata, conversation_data } = activeConversation;
    setChatState((prev) => ({ ...prev, userFormResults: conversation_metadata.userFormResults }));
    const msgs: Message[] = [];
    const { userPrompts } = conversation_metadata;
    const { output } = conversation_data;
    const total = Math.max(userPrompts.length, output.length);
    for (let i = 0; i < total; i++) {
      if (i < userPrompts.length) msgs.push({ text: userPrompts[i], sender: "user" });
      if (i < output.length) msgs.push({ text: output[i], sender: "bot" });
    }
    const isSameConv = lastConvIdForBoundaryRef.current === activeConversation.id;
    lastConvIdForBoundaryRef.current = activeConversation.id;
    setMessages(msgs);

    if (activeConversation.plan_phase && activeConversation.plan_phase !== "intake") {
      const persistedBoundary = activeConversation.conversation_metadata?.intake_boundary;
      if (typeof persistedBoundary === "number") {
        // Server pinned the boundary at consolidation time. Trust it —
        // refinement turns append on both sides but the boundary stays put.
        setIntakeHistoryBoundary(persistedBoundary);
      } else {
        // Legacy conversation (created before intake_boundary was persisted).
        // Within the same conv, keep the previously-set boundary so refinement
        // turns are revealed correctly. On a fresh load of a legacy conv,
        // pin at the current message count (the imperfection: refinement
        // turns made between server-side persistence and this load are
        // collapsed too — but the next refinement self-heals via the
        // controller's defensive backfill).
        setIntakeHistoryBoundary((prev) => (isSameConv && prev !== null ? prev : msgs.length));
      }
    } else {
      setIntakeHistoryBoundary(null);
    }
    setInputMode(true);

    // Hydrate attachments from server-side source_media_with_context (A5 + C10).
    // Server is source of truth; clear local first, then partition by media type.
    dispatch(resetScenarioHelperContextPdfs());
    dispatch(resetScenarioHelperContextImages());
    const sourceMedia = activeConversation.source_media_with_context || [];
    sourceMedia.forEach((m) => {
      const isImage = (m?.type || "").toLowerCase().startsWith("image");
      if (isImage) {
        dispatch(addScenarioHelperContextImage(m));
      } else {
        dispatch(addScenarioHelperContextPdf(m));
      }
    });

    // Restore RAG agent IDs from conversation metadata, or clear selection
    const storedRagAgentIds = activeConversation.conversation_metadata?.rag_agent_ids;
    if (Array.isArray(storedRagAgentIds) && storedRagAgentIds.length > 0) {
      dispatch(setScenarioHelperRagAgentIds(storedRagAgentIds));
    } else {
      dispatch(resetScenarioHelperRagAgentIds());
    }
  }, [activeConversation, dispatch]);

  // Direct URL navigation to /courses/:pg/list/:project is unsupported by the
  // app: every other entry point pre-hydrates `selectedProjectGroup` /
  // `currentProject` / project permissions before navigating (see
  // ProjectsList.tsx:94-102). Mirror that pattern here so the success card's
  // "Voir le module" CTA lands on the scenario graph with the right state.
  const handleOpenGeneratedModule = useCallback(async () => {
    const pgId = activeConversation?.lastGeneratedProjectGroupId;
    const projectId = activeConversation?.lastGeneratedProjectId;
    if (!pgId || isOpeningModule) return;
    setIsOpeningModule(true);
    try {
      const pgRes = await dispatch(
        fetchSpecificProjectGroupWithProjects({
          projectGroupId: pgId,
          componentId: GPT_CONVERSATION_LOADER_COMPONENT_ID,
        }),
      ).unwrap();
      const pg = pgRes?.data;
      const project = projectId
        ? pg?.project_group_elements?.find((pge) => Number(pge.project?.id) === Number(projectId))
            ?.project
        : undefined;
      if (project && projectId) {
        dispatch(setCurrentProject(project as unknown as IProject));
        const permRes = await dispatch(
          getProjectPermissionsFromServer({
            projectId: Number(projectId),
            componentId: GPT_CONVERSATION_LOADER_COMPONENT_ID,
          }),
        );
        dispatch(setCurrentProjectPermissions((permRes.payload as any)?.data ?? []));
        navigate(`/courses/${pgId}/list/${projectId}/graph`);
      } else {
        navigate(`/courses/${pgId}/list`);
      }
    } catch (e) {
      dispatch(
        setAlert({
          msg: "Impossible d'ouvrir le module. Réessayez dans un instant.",
          type: "warning",
        }),
      );
    } finally {
      setIsOpeningModule(false);
    }
  }, [activeConversation, dispatch, isOpeningModule, navigate]);

  const resetChatLocally = useCallback(() => {
    setChatState(initialState);
    setIsBotTyping(false);
    setUserInput("");
    setSessionConversationId(null);

    if (mode === "wixarpedia") {
      localStorage.removeItem("wixarpediaChatMessages");
      setMessages([{ text: "Bonjour ! Comment puis-je vous aider aujourd'hui ?", sender: "bot" }]);
      return;
    }

    // scenarioHelper mode
    dispatch(resetActiveGptConversation());
    dispatch(resetScenarioHelperRagAgentIds());
    dispatch(resetScenarioHelperContextPdfs());
    dispatch(resetScenarioHelperContextImages());

    setMessages([{ text: "Bonjour. Quelle est votre problématique ?", sender: "bot" }]);
    setInputMode(true);

    const savedProfile = getSavedAiProfile();
    if (savedProfile?.metier) {
      // Profile already saved — inject into state silently
      setChatState((prev) => ({
        ...prev,
        userFormResults: {
          metier: savedProfile.metier,
          targetAudience: savedProfile.targetAudience,
        },
      }));
    } else {
      // No saved profile — show onboarding modal
      setOnboardingStep("metier");
      setOnboardingMetier("");
      setOnboardingTargetAudience("");
      setShowOnboardingModal(true);
    }
  }, [mode, dispatch, getSavedAiProfile]);

  const updateBotMessage = useCallback(
    (chunk: string) => {
      setMessages((prev) => {
        const msgs = [...prev];
        const i = msgs.length - 1;
        msgs[i] = { ...msgs[i], text: msgs[i].text + chunk };
        return msgs;
      });
      if (ttsSessionOpenRef.current) {
        conversation.pushSpeakingChunk(chunk);
      }
    },
    [conversation],
  );

  const saveAiProfileToPreferences = useCallback(
    (formResults: { metier?: string; targetAudience?: string }) => {
      if (!user?.id || !formResults.metier) return;
      try {
        const prefs = (user as any)?.preferences;
        const base =
          typeof prefs === "string"
            ? JSON.parse(prefs) ?? {}
            : typeof prefs === "object" && prefs !== null
            ? { ...prefs }
            : {};
        base.aiProfile = {
          metier: formResults.metier,
          targetAudience: formResults.targetAudience,
        };
        dispatch(
          updateUser({
            id: Number(user.id),
            preferences: base,
          } as any),
        );
      } catch (e) {
        console.error("Failed to save AI profile:", e);
      }
    },
    [user, dispatch],
  );

  const handleOnboardingMetierSelect = useCallback((value: string) => {
    if (value === "Autre") {
      setOnboardingShowCustomMetier(true);
      setOnboardingCustomMetier("");
    } else {
      setOnboardingMetier(value);
      setOnboardingShowCustomMetier(false);
      setOnboardingStep("targetAudience");
    }
  }, []);

  const handleCustomMetierSubmit = useCallback(() => {
    const value = onboardingCustomMetier.trim();
    if (!value) return;
    setOnboardingMetier(value);
    setOnboardingShowCustomMetier(false);
    setOnboardingStep("targetAudience");
  }, [onboardingCustomMetier]);

  const handleOnboardingSubmit = useCallback(() => {
    const formResults = {
      metier: onboardingMetier,
      targetAudience: onboardingTargetAudience.trim() || undefined,
    };
    setChatState((prev) => ({
      ...prev,
      userFormResults: formResults,
    }));
    setShowOnboardingModal(false);
    saveAiProfileToPreferences(formResults);
  }, [onboardingMetier, onboardingTargetAudience, saveAiProfileToPreferences]);

  const handleOnboardingSkip = useCallback(() => {
    setShowOnboardingModal(false);
  }, []);

  const handleUserPromptSubmitWithText = useCallback(
    async (text: string) => {
      if (!text.trim()) return;
      const prompt = text;
      setUserInput("");
      setIsSubmitting(true);
      setMessages((prev) => [
        ...prev,
        { text: prompt, sender: "user" },
        { text: "", sender: "bot" },
      ]);
      setIsBotTyping(true);

      // Open the TTS streaming session BEFORE the LLM call so the WS handshake
      // overlaps with the bot's first tokens. Chunks are pushed live via
      // updateBotMessage; the session is closed in the finally block.
      if (conversation.voiceModeEnabled) {
        ttsSessionOpenRef.current = true;
        conversation.beginSpeakingSession().catch((err) => {
          console.error("Voice mode session error:", err);
          ttsSessionOpenRef.current = false;
        });
      }

      try {
        if (mode === "scenarioHelper") {
          const conversationId = activeConversationRef.current?.id ?? sessionConversationId;
          const currentPhase = phaseRef.current;
          const sourceMediaIds = [
            ...contextPdfs.map((m) => Number(m.id)),
            ...contextImages.map((m) => Number(m.id)),
          ];

          if (!conversationId) {
            // New conversation → always start in plan mode (D2).
            const result = await dispatch(
              startPlanModeChat({
                userPrompt: prompt,
                userFormResults: chatState.userFormResults,
                sourceMediasWithContext: sourceMediaIds,
                chosenLlmProvider,
                ragAgentIds,
                sourceMedias: [...contextPdfs, ...contextImages],
                cb: updateBotMessage,
                cbOnRequestEnd: () => {
                  dispatch(
                    fetchGptConversations({
                      componentId: GPT_CONVERSATION_LOADER_COMPONENT_ID,
                      start: 0,
                    }),
                  );
                  dispatch(syncUserWithServer({}));
                },
              }),
            );
            const created = result.payload as IGptConversation | undefined;
            if (created?.id) {
              setSessionConversationId(created.id);
            }
          } else if (currentPhase === "intake") {
            const intakeResult = (await dispatch(
              continuePlanModeChat({
                userPrompt: prompt,
                conversationId,
                ragAgentIds,
                sourceMediasWithContext: sourceMediaIds,
                cb: updateBotMessage,
              }),
            )) as any;
            dispatch(syncUserWithServer({}));
            // The intake LLM may emit a tool call signaling readiness to
            // consolidate. The backend has already validated and persisted
            // a synthetic ack; here we just chain into transitionToReady so
            // the in-progress panel takes over without the user having to
            // click anything.
            const toolEvent = intakeResult?.payload?.toolEvent as
              | { event: string; accepted: boolean; reason?: string }
              | null
              | undefined;
            if (toolEvent?.event === "transition_requested" && toolEvent.accepted) {
              const transitionResult = (await dispatch(
                transitionToReady({ conversationId, ragAgentIds }),
              )) as any;
              if (transitionResult?.error) {
                dispatch(
                  setAlert({
                    type: "error",
                    msg: transitionResult?.payload || "Échec de la consolidation du plan.",
                    customTimeoutInMs: 5000,
                  }),
                );
              }
              // The boundary is server-authoritative now: transitionToReady
              // persists conversation_metadata.intake_boundary, then the
              // syncUserWithServer above (and the activeConversation
              // re-fetch chained inside transitionToReady) feeds the value
              // into loadPreviousConversation. No manual set needed.
            }
          } else if (currentPhase === "ready_to_generate") {
            // Deterministic NL trigger for the plan → generated transition:
            // when the user explicitly says "génère / vas-y / let's go…",
            // skip the refinement turn and open the generation modal so
            // they can confirm PG / image-gen settings. Same regex as the
            // intake → plan trigger (see utils/proceedIntent.ts).
            if (userMessageSignalsProceed(prompt)) {
              updateBotMessage("OK, j'ouvre la fenêtre de génération du module.");
              dispatch(openStandardModal(createProjectFromAiGraphModalId));
              return;
            }
            // Refinement turn — JSON response, not streamed; show the final
            // server confirmation as the bot reply.
            const result = (await dispatch(
              refinePlanThunk({ conversationId, message: prompt, ragAgentIds }),
            )) as any;
            if (result?.error) {
              dispatch(
                setAlert({
                  type: "error",
                  msg: result?.payload || "Échec du raffinement — réessayez.",
                  customTimeoutInMs: 5000,
                }),
              );
              updateBotMessage("Échec du raffinement — réessayez.");
              return;
            }
            const refinedPlan = result?.payload?.plan;
            const failedImages = (refinedPlan?.attachedImages || []).filter(
              (img: any) => img?.failed,
            );
            if (failedImages.length > 0) {
              dispatch(
                setAlert({
                  type: "warning",
                  msg: "L'image jointe n'a pas pu être analysée; le plan a été mis à jour sans elles.",
                  customTimeoutInMs: 5000,
                }),
              );
            }
            const mediaTypeOverrides = (result?.payload?.mediaTypeOverrides ?? []) as Array<{
              sceneTitle: string;
              previousMediaType: "image360" | "video360";
              mediaType: "image360" | "video360";
            }>;
            const switchesToVideo = mediaTypeOverrides.filter((o) => o.mediaType === "video360");
            if (switchesToVideo.length > 0) {
              const titles = switchesToVideo.map((o) => `« ${o.sceneTitle} »`).join(", ");
              dispatch(
                setAlert({
                  type: "warning",
                  msg: `${titles} ${
                    switchesToVideo.length > 1 ? "passent" : "passe"
                  } en vidéo 360°. Wixar ne génère pas encore de vidéos — un média de remplacement sera utilisé que vous pourrez remplacer manuellement après la génération.`,
                  customTimeoutInMs: 8000,
                }),
              );
            }
            // The reducer pushes "__PLAN__" to output and the user message
            // to userPrompts. loadPreviousConversation rebuilds the local
            // messages array from Redux, rendering the new plan inline.
            // Push the sentinel to local messages immediately so the user
            // sees the updated plan without waiting for the useEffect cycle.
            setMessages((prev) => {
              const msgs = [...prev];
              // Replace the empty bot bubble (pushed at submit time) with
              // the plan sentinel so renderedMessages picks it up.
              const lastIdx = msgs.length - 1;
              if (lastIdx >= 0 && msgs[lastIdx].sender === "bot") {
                msgs[lastIdx] = { text: "__PLAN__", sender: "bot" };
              }
              return msgs;
            });
          } else if (currentPhase === null) {
            await dispatch(
              updateChat({
                userPrompt: prompt,
                conversationId,
                ragAgentIds,
                cb: updateBotMessage,
              }),
            );
            dispatch(syncUserWithServer({}));
          } else {
            // generating / generated — input is disabled at the UI layer.
          }
        } else {
          await dispatch(askWixarpedia({ userPrompt: prompt, cb: updateBotMessage }));
        }
      } finally {
        if (ttsSessionOpenRef.current) {
          conversation.endSpeakingSession();
          ttsSessionOpenRef.current = false;
        }
        setIsSubmitting(false);
        setIsBotTyping(false);
      }
    },
    [
      activeConversation,
      sessionConversationId,
      chatState.userFormResults,
      dispatch,
      mode,
      chosenLlmProvider,
      ragAgentIds,
      contextPdfs,
      contextImages,
      updateBotMessage,
      conversation,
    ],
  );

  const handleUserPromptSubmit = useCallback(() => {
    // User-initiated commit (Enter / Send): treat as "I'm done — go."
    // Stop the mic, drop accumulated buffer so onEnd won't auto-resend,
    // disarm the pending auto-listen flag, and silence any in-flight TTS.
    if (conversation.voiceModeEnabled) {
      dictation.resetTranscript();
      dictation.stopListening().catch(() => undefined);
      conversation.disarmAutoListen();
      conversation.stopSpeaking();
    }
    return handleUserPromptSubmitWithText(userInput);
  }, [userInput, handleUserPromptSubmitWithText, conversation, dictation]);

  // Wire the auto-send path: dictation's onTranscript (closing over an outdated
  // submit) reads the current submit fn through this ref each time it fires.
  submitWithTextRef.current = handleUserPromptSubmitWithText;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (userInput.trim() && !isSubmitting) handleUserPromptSubmit();
      }
    },
    [userInput, isSubmitting, handleUserPromptSubmit],
  );

  const visibleMessages = useMemo(() => {
    if (isPlanMode && phase !== "intake" && intakeHistoryBoundary !== null && !showIntakeHistory) {
      return messages.slice(intakeHistoryBoundary);
    }
    return messages;
  }, [messages, isPlanMode, phase, intakeHistoryBoundary, showIntakeHistory]);

  // Find the index of the last __PLAN__ marker so we can show the CTA
  // only on the most recent plan.
  const lastPlanIdx = useMemo(() => {
    for (let i = visibleMessages.length - 1; i >= 0; i--) {
      if (visibleMessages[i].sender === "bot" && visibleMessages[i].text === "__PLAN__") {
        return i;
      }
    }
    return -1;
  }, [visibleMessages]);

  const renderedMessages = useMemo(
    () =>
      visibleMessages.map((message, idx) => {
        // Plan sentinel: render PlanPreview inline instead of a text bubble.
        if (message.sender === "bot" && message.text === "__PLAN__") {
          const isLatestPlan = idx === lastPlanIdx;
          return (
            <div key={idx}>
              {isLatestPlan && plan ? (
                <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                  <PlanPreview plan={plan} />
                  <div className="px-3 pb-3 pt-1">
                    <GraphBubble
                      markdown={renderPlanToMarkdown(plan)}
                      mdStringId={`plan-${activeConversation?.id ?? "x"}-${idx}`}
                      flat
                    />
                  </div>
                  {phase === "ready_to_generate" && (
                    <div className="px-3 pb-3 pt-2 flex flex-col items-center gap-2">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 italic">
                        <IoMdInformationCircleOutline className="shrink-0" />
                        <span>
                          Vous pouvez générer le module maintenant, ou continuer la conversation
                          pour affiner le plan.
                        </span>
                      </div>
                      <button
                        className="btn-primary-fill text-sm"
                        disabled={isSubmitting}
                        onClick={() =>
                          isPlanMode || !onGenerateProject
                            ? dispatch(openStandardModal(createProjectFromAiGraphModalId))
                            : onGenerateProject()
                        }
                      >
                        Générer le module
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex justify-start">
                  <div className="px-3 py-1.5 rounded-md bg-gray-100 border border-gray-200 text-xs text-gray-400 italic">
                    Plan précédent (remplacé)
                  </div>
                </div>
              )}
            </div>
          );
        }

        // Skip empty user prompts (system-inserted for plan consolidation).
        if (message.sender === "user" && !message.text.trim()) {
          return null;
        }

        return (
          <div key={idx}>
            <ChatBubble customKey={idx} sender={message.sender} text={message.text} />
            {!isPlanMode && isMarkdownShapedLikeAScenario(message.text) && (
              <>
                <GraphBubble
                  markdown={message.text}
                  mdStringId={generateHashFromString(message.text)}
                />
                {!isSubmitting && (
                  <div className="flex w-full justify-center">
                    {message.text && (
                      <GeneratePdfFromScenarioHelperResponse
                        markdown={message.text}
                        key={`${message.text}-${idx}`}
                      />
                    )}
                    <button
                      className="btn-primary-outline mt-1 w-3/5"
                      onClick={() =>
                        onGenerateProject
                          ? onGenerateProject()
                          : dispatch(openStandardModal(createProjectFromAiGraphModalId))
                      }
                    >
                      {t("pages.chat.generateModuleFromGraph")}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        );
      }),
    [
      visibleMessages,
      lastPlanIdx,
      plan,
      phase,
      isSubmitting,
      dispatch,
      t,
      isPlanMode,
      onGenerateProject,
    ],
  );

  // Skip-intake threshold (R2). Backend mirrors this rule.
  const typedUserTurns = useMemo(
    () => messages.filter((m) => m.sender === "user" && m.text.trim().length > 0).length,
    [messages],
  );
  const totalUserTurns = useMemo(
    () => messages.filter((m) => m.sender === "user").length,
    [messages],
  );
  const attachmentCount = contextPdfs.length + contextImages.length;
  const skipState = useMemo(
    () => canSkipIntake({ typedUserTurns, totalUserTurns, attachmentCount }),
    [typedUserTurns, totalUserTurns, attachmentCount],
  );

  const handleSkipIntake = useCallback(async () => {
    if (!activeConversation?.id) return;
    setIsBotTyping(true);
    setIsSubmitting(true);
    try {
      const result = (await dispatch(
        transitionToReady({ conversationId: activeConversation.id, ragAgentIds }),
      )) as any;
      if (result?.error) {
        dispatch(
          setAlert({
            type: "error",
            msg: result?.payload || "Échec de la consolidation du plan.",
            customTimeoutInMs: 5000,
          }),
        );
        return;
      }
      const planResult = result?.payload?.plan;
      const failedImages = (planResult?.attachedImages || []).filter((img: any) => img?.failed);
      if (failedImages.length > 0) {
        dispatch(
          setAlert({
            type: "warning",
            msg: "L'image jointe n'a pas pu être analysée; le plan a été généré sans elles.",
            customTimeoutInMs: 5000,
          }),
        );
      }
      // Boundary is persisted by the server inside transitionToReady;
      // loadPreviousConversation reads it back via syncUserWithServer.
    } finally {
      setIsBotTyping(false);
      setIsSubmitting(false);
    }
  }, [activeConversation?.id, dispatch, ragAgentIds]);

  return (
    <div className="flex h-full">
      <div className="flex m-2 w-full flex-col items-center">
        <div className="w-full h-full p-4 bg-white shadow-lg rounded-lg flex flex-col relative">
          {showOnboardingModal && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 rounded-lg backdrop-blur-sm">
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 w-[460px] max-w-[90%] p-6">
                <h3 className="text-lg font-medium mb-1">{t("pages.chat.onboarding.title")}</h3>
                <p className="text-sm text-gray-500 mb-5">{t("pages.chat.onboarding.subtitle")}</p>

                {onboardingStep === "metier" && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-3">
                      {t("pages.chat.onboarding.metierQuestion")}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(paths.metier.options || []).map((opt) => (
                        <button
                          key={opt.value}
                          className={`px-4 py-2 rounded-lg border text-sm transition-colors ${
                            opt.value === "Autre" && onboardingShowCustomMetier
                              ? "border-green-500 bg-green-50"
                              : "border-gray-300 hover:bg-green-50 hover:border-green-500"
                          }`}
                          onClick={() => handleOnboardingMetierSelect(opt.value)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {onboardingShowCustomMetier && (
                      <div className="flex gap-2 mt-3">
                        <input
                          type="text"
                          value={onboardingCustomMetier}
                          onChange={(e) => setOnboardingCustomMetier(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && onboardingCustomMetier.trim()) {
                              e.preventDefault();
                              handleCustomMetierSubmit();
                            }
                          }}
                          placeholder={t("pages.chat.onboarding.customMetierPlaceholder")}
                          className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                          autoFocus
                        />
                        <button
                          className="btn-primary-fill text-sm"
                          onClick={handleCustomMetierSubmit}
                          disabled={!onboardingCustomMetier.trim()}
                        >
                          OK
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {onboardingStep === "targetAudience" && (
                  <div>
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 mb-4 rounded-full bg-green-50 text-green-700 text-xs font-medium">
                      {onboardingMetier}
                    </div>
                    <p className="text-sm font-medium text-gray-700 mb-3">
                      {t("pages.chat.onboarding.targetAudienceQuestion")}
                    </p>
                    <input
                      type="text"
                      value={onboardingTargetAudience}
                      onChange={(e) => setOnboardingTargetAudience(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleOnboardingSubmit();
                        }
                      }}
                      placeholder={t("pages.chat.onboarding.targetAudiencePlaceholder")}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                      autoFocus
                    />
                  </div>
                )}

                <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-100">
                  {onboardingStep === "metier" ? (
                    <>
                      <button
                        className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={handleOnboardingSkip}
                      >
                        {t("pages.chat.onboarding.skip")}
                      </button>
                      <div />
                    </>
                  ) : (
                    <>
                      <button
                        className="btn-alternative-outline text-sm"
                        onClick={() => setOnboardingStep("metier")}
                      >
                        {t("general.back")}
                      </button>
                      <button className="btn-primary-fill" onClick={handleOnboardingSubmit}>
                        {t("pages.chat.onboarding.start")}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {mode === "scenarioHelper" && (aiContextParts.length > 0 || !orgCtx || !hasUserProfile) && (
            <div className="flex items-center justify-between px-3 py-1.5 mb-2 bg-gray-50 rounded-lg border border-gray-200 text-xs text-gray-500">
              <span className="truncate">
                {aiContextParts.length > 0 && (
                  <>
                    {t("pages.chat.aiContext")} {aiContextParts.join(" · ")}
                    {(!orgCtx || !hasUserProfile) && <span className="ml-2 text-gray-400">—</span>}
                  </>
                )}
                {!hasUserProfile && !orgCtx && (
                  <span className="text-amber-500">
                    {t("pages.chat.aiContextHintBoth")}{" "}
                    <Link to="/profile/me" className="underline hover:text-amber-600">
                      {t("pages.chat.aiContextHintProfile")}
                    </Link>{" "}
                    {t("pages.chat.aiContextHintAnd")}{" "}
                    <Link to="/profile/organization" className="underline hover:text-amber-600">
                      {t("pages.chat.aiContextHintOrg")}
                    </Link>{" "}
                    {t("pages.chat.aiContextHintSuffix")}
                  </span>
                )}
                {hasUserProfile && !orgCtx && (
                  <Link to="/profile/organization" className="text-amber-500 hover:text-amber-600">
                    {t("pages.chat.aiContextOrgHint")}
                  </Link>
                )}
                {!hasUserProfile && orgCtx && (
                  <Link to="/profile/me" className="text-amber-500 hover:text-amber-600">
                    {t("pages.chat.aiContextProfileHint")}
                  </Link>
                )}
              </span>
              {(hasUserProfile || orgCtx) && (
                <Link
                  to="/profile/me"
                  className="ml-3 shrink-0 text-purple-500 hover:text-purple-600 font-medium"
                >
                  {t("general.edit")}
                </Link>
              )}
            </div>
          )}
          {mode === "scenarioHelper" && isPlanMode && (
            <PlanModeProgress
              step={
                phase === "intake"
                  ? 1
                  : phase === "ready_to_generate" || phase === "generating"
                  ? 2
                  : phase === "generated"
                  ? 4
                  : 3
              }
            />
          )}
          {isLegacyConversation && !legacyBannerDismissed && (
            <div className="flex items-start gap-3 px-3 py-2 mb-2 rounded-md bg-amber-50 border border-amber-300 text-amber-900">
              <div className="flex-1 text-xs leading-relaxed">
                <div className="font-semibold mb-0.5">Conversation antérieure à Scenaria v2</div>
                <div>
                  Cette conversation utilise l&apos;ancien flux. Pour bénéficier du plan de scènes
                  interactif, des générations 360° par scène et du suivi de génération en temps
                  réel,{" "}
                  <button
                    type="button"
                    className="text-purple-500 hover:text-purple-600 font-medium underline"
                    onClick={resetChatLocally}
                  >
                    démarrez une nouvelle requête
                  </button>
                  .
                </div>
              </div>
              <button
                type="button"
                aria-label="Masquer ce message"
                className="text-amber-700 hover:text-amber-900 text-lg leading-none px-1"
                onClick={() => setLegacyBannerDismissed(true)}
              >
                ×
              </button>
            </div>
          )}
          <div className="chat-window bg-gray-100 p-4 flex-grow overflow-y-scroll rounded-md flex flex-col space-y-4">
            {mode === "scenarioHelper" &&
              isPlanMode &&
              phase !== "intake" &&
              intakeHistoryBoundary !== null && (
                <button
                  onClick={() => setShowIntakeHistory((v) => !v)}
                  className="text-xs text-gray-500 hover:text-gray-700 underline self-start"
                >
                  {showIntakeHistory
                    ? "Masquer l'historique des échanges"
                    : "Voir l'historique des échanges"}
                </button>
              )}
            {mode === "scenarioHelper" &&
              isPlanMode &&
              phase !== "intake" &&
              (contextPdfs.length > 0 || contextImages.length > 0) && (
                <div className="flex flex-wrap items-center gap-1.5 px-2 py-1.5 rounded-md bg-gray-200/60 text-[11px] text-gray-500">
                  <MdOutlineAttachment size={13} className="shrink-0" />
                  {contextPdfs.map((pdf) => (
                    <span key={pdf.id} className="truncate max-w-[140px]">
                      {pdf.filename || pdf.name}
                    </span>
                  ))}
                  {contextImages.map((img) => (
                    <span key={img.id} className="truncate max-w-[140px]">
                      {img.filename || img.name}
                    </span>
                  ))}
                </div>
              )}
            {/* Fallback for legacy conversations consolidated before __PLAN__
                sentinel was introduced — render the current plan inline. */}
            {mode === "scenarioHelper" &&
              isPlanMode &&
              phase === "ready_to_generate" &&
              plan &&
              lastPlanIdx === -1 && (
                <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                  <PlanPreview plan={plan} />
                  <div className="px-3 pb-3 pt-1">
                    <GraphBubble
                      markdown={renderPlanToMarkdown(plan)}
                      mdStringId={`plan-fallback-${activeConversation?.id ?? "x"}`}
                      flat
                    />
                  </div>
                  <div className="px-3 pb-3 pt-2 flex flex-col items-center gap-2">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 italic">
                      <IoMdInformationCircleOutline className="shrink-0" />
                      <span>
                        Vous pouvez générer le module maintenant, ou continuer la conversation pour
                        affiner le plan.
                      </span>
                    </div>
                    <button
                      className="btn-primary-fill text-sm"
                      disabled={isSubmitting}
                      onClick={() =>
                        isPlanMode || !onGenerateProject
                          ? dispatch(openStandardModal(createProjectFromAiGraphModalId))
                          : onGenerateProject()
                      }
                    >
                      Générer le module
                    </button>
                  </div>
                </div>
              )}
            {renderedMessages}
            {mode === "scenarioHelper" && phase !== "generating" && planProgress?.message && (
              <div className="flex justify-start">
                <div className="text-xs text-gray-500 italic px-1">{planProgress.message}</div>
              </div>
            )}
            {isBotTyping && (
              <div className="flex justify-start">
                <div className="px-4 py-3 rounded-xl rounded-bl-sm bg-gray-50 border border-gray-200 text-gray-500">
                  <TypingIndicator />
                </div>
              </div>
            )}
            {mode === "scenarioHelper" && phase === "intake" && activeConversation?.id && (
              <SkipIntakeButton
                canSkip={skipState.canSkip}
                reason={skipState.reason}
                isSubmitting={isSubmitting}
                onClick={handleSkipIntake}
              />
            )}
            {mode === "scenarioHelper" &&
              (phase === "generating" || phase === "generated") &&
              plan && (
                <GenerationRecap
                  plan={plan}
                  phase={phase}
                  progressMessage={planProgress?.message ?? null}
                  sourceMedia={[...contextImages, ...contextPdfs]}
                  lastGeneratedProjectGroupId={activeConversation?.lastGeneratedProjectGroupId}
                  isOpeningModule={isOpeningModule}
                  onOpenModule={handleOpenGeneratedModule}
                  onNewScenario={resetChatLocally}
                  generationStartedAt={generationStartedAt}
                />
              )}
            {mode === "scenarioHelper" &&
              !activeConversation &&
              messages.length <= 1 &&
              !isBotTyping && (
                <div className="flex-1 flex items-center justify-center">
                  <p className="text-sm text-gray-400 text-center max-w-md">
                    {t("pages.chat.scenarioHelperEmptyHint")}
                  </p>
                </div>
              )}
            {/* Inline form buttons removed — onboarding modal used instead */}
            <div ref={bottomRef} />
          </div>
          {inputMode && (
            <div className="flex-shrink-0 border-t border-gray-200 px-4 pt-3 pb-2 bg-white">
              {/* Single row: attach controls (scenarioHelper only) on the
                  left, SpeakingIndicator + VoiceModeToggle on the right.
                  Wraps two previous rows into one — selected-media chips
                  inside AttachContextMedia now share horizontal space
                  with the voice toggle. items-start keeps the toggle
                  anchored at the top when chips stack. */}
              <div className="flex items-start justify-between gap-3 mb-1.5 min-h-[28px]">
                <div className="flex-1 min-w-0">
                  {mode === "scenarioHelper" && (
                    <AttachContextMedia
                      isEditionEnabled={(!phase || phase === "intake") && !isSubmitting}
                    />
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <SpeakingIndicator
                    isSpeaking={conversation.isSpeaking}
                    onStop={conversation.stopSpeaking}
                  />
                  <VoiceModeToggle
                    enabled={conversation.voiceModeEnabled}
                    onToggle={conversation.toggleVoiceMode}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              <div className="flex gap-2 items-end">
                <textarea
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  readOnly={dictation.isListening}
                  className={`flex-1 resize-none rounded-xl border px-4 py-2.5 text-sm focus:outline-none focus:ring-1 disabled:bg-gray-100 disabled:text-gray-400 transition-all overflow-y-auto max-h-40 ${
                    dictation.isListening
                      ? "border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-green-500 focus:ring-green-500"
                  }`}
                  placeholder={
                    mode === "scenarioHelper"
                      ? phase === "generated"
                        ? "Module généré — démarrez une nouvelle requête pour un autre scénario"
                        : phase === "generating"
                        ? "Génération en cours…"
                        : phase === "ready_to_generate"
                        ? "Continuez la conversation pour affiner le plan"
                        : t("pages.chat.scenarioHelperInputPlaceholder")
                      : t("pages.chat.wixarpediaInputPlaceholder")
                  }
                  disabled={isSubmitting || phase === "generating" || phase === "generated"}
                />
                <MicrophoneButton
                  isListening={dictation.isListening}
                  isSupported={dictation.isSupported}
                  disabled={isSubmitting}
                  idleTooltipKey={
                    conversation.voiceModeEnabled
                      ? "voice.conversationStartTooltip"
                      : "voice.dictateTooltip"
                  }
                  listeningTooltipKey={
                    conversation.voiceModeEnabled
                      ? "voice.conversationListeningTooltip"
                      : "voice.dictateListeningTooltip"
                  }
                  cooldown={
                    conversation.voiceModeEnabled
                      ? { triggerAt: dictation.lastActivityAt, durationMs: 1500 }
                      : undefined
                  }
                  onStartListening={() => {
                    // Barge-in: if the bot is speaking and conversation mode is on,
                    // stop TTS so the mic can take over cleanly.
                    if (conversation.voiceModeEnabled && conversation.isSpeaking) {
                      conversation.stopSpeaking();
                    }
                    dictation.resetTranscript();
                    dictation
                      .startListening()
                      .catch((err) => console.error("STT start error:", err));
                  }}
                  onStopListening={() => {
                    // Re-click while listening = stop only, no send (both modes).
                    // Disarm the conversation auto-listen cue so a stale flag
                    // doesn't re-arm the mic later.
                    conversation.disarmAutoListen();
                    dictation.stopListening().catch(() => undefined);
                  }}
                />
                <Tooltip title={submitDisabledReason} disableHoverListener={!submitDisabledReason}>
                  <span>
                    <button
                      onClick={handleUserPromptSubmit}
                      disabled={isSubmitDisabled}
                      className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-green-600 rounded-xl transition-colors"
                    >
                      <IoMdSend size={16} />
                    </button>
                  </span>
                </Tooltip>
              </div>
              <div className="flex justify-between items-center mt-1.5">
                {mode === "scenarioHelper" && (
                  <LlmProviderDropdown
                    defaultValue={chosenLlmProvider}
                    onChange={(value) => dispatch(setChosenLlmProvider(value))}
                    disabled={Boolean(activeConversation) || isSubmitting}
                  />
                )}
                <p className="text-xs text-gray-400 ml-auto">
                  Shift+Entrée pour un retour à la ligne
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      {isPlanMode && !onGenerateProject && (
        <ModalStandard
          id={createProjectFromAiGraphModalId}
          isStatic
          modalType="standard"
          className="w-[60%] h-auto"
          showCloseButton
        >
          <CreateProjectFromAiGraph />
        </ModalStandard>
      )}
      <IsLoading componentId={CHATBOT_COMPONENT_ID} showSuccess={false} />

      {/* Onboarding modal removed — inline overlay used instead */}
    </div>
  );
};

export default React.memo(Chatbot);
